import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { AuthContext, capitalize, useApi } from '@repo-level/core-front';
import { useTranslation } from 'react-i18next';
import { Form, FormApi } from 'informed';

import { sendContactMessage, SupportForm } from 'api/support';
import { Button } from 'components/buttons';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { TextArea } from 'components/form/TextArea';
import { ModalDefaultProps } from 'components/Modal';
import { showTooltip } from 'helpers/tooltip';
import { translationFactory } from 'helpers/trans';
import { required, validateEmail } from 'helpers/validators';
import { TooltipTypes } from 'modules/globalTooltip';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { ModalTemplate } from 'components/ModalTemplate';

import { ScFormSubmitGroup } from 'components/form/groups/styled';

export const Support = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const { t } = useTranslation('common');

    const { isMobileS } = useContext(CommonUtilsContext);

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? { type: 'mobile', mode: { type: 'default' }, headlineGapSm: true }
        : {
            type: 'desktop',
            size: 'small',
            mode: { type: 'default' },
            headlineGapSm: true,
          };
    }, [isMobileS]);

    return (
      <ModalTemplate
        ref={ref}
        onClose={onClose}
        settings={modalSettings}
        title={capitalize(t('support.title'))}
        description={t('support.writeUsMessage')}
      >
        <SupportContainer />
      </ModalTemplate>
    );
  }
);

const SupportContainer: FC = () => {
  const { t } = useTranslation();

  const { user } = useContext(AuthContext);

  const [requestSendMessage, { pending }] = useApi(sendContactMessage);

  const formApiRef = useRef<FormApi<SupportForm>>();

  const handleSubmit = useCallback(
    async (values: SupportForm) => {
      const { error } = await requestSendMessage(values).request;

      if (error == null) {
        formApiRef.current?.reset();

        showTooltip({
          type: TooltipTypes.SUCCESS,
          translate: true,
          title: translationFactory('common:support.tooltip.success'),
        });
      } else {
        showTooltip({
          type: TooltipTypes.ERROR,
          translate: true,
          title: translationFactory('common:support.tooltip.failure'),
        });
      }
    },
    [formApiRef, requestSendMessage]
  );

  return (
    <Form<SupportForm>
      getApi={(api) => {
        formApiRef.current = api;
      }}
      onSubmit={handleSubmit}
    >
      <FormGroup>
        <Input
          key={user?.email}
          field="email"
          validate={validateEmail}
          initialValue={user?.email}
          disabled={user?.email != null}
          animatedPlaceholder={t('glossary:email')}
          validateOnBlur
        />
      </FormGroup>
      <FormGroup>
        <TextArea
          field="content"
          validate={required}
          animatedPlaceholder={t('glossary:message')}
          validateOnBlur
        />
      </FormGroup>
      <ScFormSubmitGroup>
        <Button type="submit" green xLarge wide pending={pending}>
          {t('glossary:sendMessage')}
        </Button>
      </ScFormSubmitGroup>
    </Form>
  );
};
